import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useTranslation } from 'react-i18next';
import './Materials.css';
import { convertYouTubeUrl, getYouTubeThumbnail, getYouTubeVideoId, getResources, getFileUrl, incrementResourceDownload } from '../services/apiService';

const Materials = () => {
  const { t } = useTranslation();
  const [allMaterials, setAllMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // États pour les filtres (communs aux documents et vidéos)
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest'); // 'newest', 'oldest', 'name'

  // Charger les ressources depuis l'API
  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setIsLoading(true);
        const resources = await getResources({ status: 'Publié' });
        
        // Transformer les ressources en format attendu
        const transformedMaterials = resources.map(resource => {
          const fileUrl = resource.file_url || (resource.file_path ? getFileUrl(resource.file_path) : '');
          const isVideo = resource.file_type === 'Video';
          const createdAt = resource.created_at ? resource.created_at.split('T')[0] : '';
          const updatedAt = resource.updated_at ? resource.updated_at.split('T')[0] : '';
          
          // Extraire youtubeId si c'est une URL YouTube
          let youtubeId = null;
          if (isVideo && fileUrl) {
            const videoId = getYouTubeVideoId(fileUrl);
            if (videoId) youtubeId = videoId;
          }
          
          // Déterminer l'icône selon le type
          const getIcon = (type) => {
            const typeLower = (type || '').toLowerCase();
            if (typeLower.includes('pdf')) return 'fa-file-pdf';
            if (typeLower.includes('word') || typeLower.includes('doc')) return 'fa-file-word';
            if (typeLower.includes('excel') || typeLower.includes('xls')) return 'fa-file-excel';
            if (typeLower.includes('powerpoint') || typeLower.includes('ppt')) return 'fa-file-powerpoint';
            if (typeLower.includes('image') || typeLower.includes('img')) return 'fa-file-image';
            if (typeLower.includes('zip') || typeLower.includes('archive')) return 'fa-file-archive';
            if (typeLower.includes('video')) return 'fa-video';
            return 'fa-file';
          };
          
          return {
            id: resource.id,
            name: resource.title,
            type: resource.file_type || 'PDF',
            uploadDate: createdAt,
            createdAt: createdAt,
            updatedAt: updatedAt !== createdAt ? updatedAt : null,
            filePath: fileUrl,
            fileUrl: fileUrl,
            youtubeId: youtubeId,
            category: isVideo ? 'video' : 'file',
            icon: getIcon(resource.file_type)
          };
        });
        
        setAllMaterials(transformedMaterials);
      } catch (error) {
        setAllMaterials([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMaterials();
  }, []);

  // Filtrer et trier tous les matériaux
  const filteredAndSortedMaterials = useMemo(() => {
    let filtered = allMaterials.filter(material => {
      // Filtre par recherche (nom/titre)
      const matchesSearch = searchTerm === '' || 
        material.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filtre par type
      const matchesType = typeFilter === '' || material.type === typeFilter;
      
      // Filtre par date
      const materialDate = material.createdAt || material.updatedAt || material.uploadDate || '';
      const matchesDate = dateFilter === '' || materialDate === dateFilter;
      
      return matchesSearch && matchesType && matchesDate;
    });
    
    // Tri
    if (sortBy === 'newest') {
      filtered.sort((a, b) => {
        const dateA = a.createdAt || a.updatedAt || a.uploadDate || '';
        const dateB = b.createdAt || b.updatedAt || b.uploadDate || '';
        return new Date(dateB) - new Date(dateA);
      });
    } else if (sortBy === 'oldest') {
      filtered.sort((a, b) => {
        const dateA = a.createdAt || a.updatedAt || a.uploadDate || '';
        const dateB = b.createdAt || b.updatedAt || b.uploadDate || '';
        return new Date(dateA) - new Date(dateB);
      });
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }));
    }
    
    return filtered;
  }, [allMaterials, searchTerm, typeFilter, dateFilter, sortBy]);

  // Filter materials by type (file vs video)
  const materials = filteredAndSortedMaterials.filter(m => m.category === 'file');
  const videos = filteredAndSortedMaterials.filter(m => m.category === 'video');
  
  // Obtenir tous les types uniques (tous matériaux confondus)
  const allTypes = useMemo(() => {
    return Array.from(new Set(allMaterials.map(m => m.type).filter(Boolean))).sort();
  }, [allMaterials]);
  
  // Obtenir toutes les dates uniques (tous matériaux confondus)
  const allDates = useMemo(() => {
    const dates = allMaterials
      .map(m => m.createdAt || m.updatedAt || m.uploadDate)
      .filter(Boolean)
      .sort()
      .reverse();
    return Array.from(new Set(dates));
  }, [allMaterials]);
  
  // Réinitialiser la pagination quand les filtres changent
  useEffect(() => {
    setCurrentPageMaterials(1);
    setCurrentPageVideos(1);
  }, [searchTerm, typeFilter, dateFilter, sortBy]);
  
  // Fonction pour gérer le téléchargement
  const handleDownload = async (material) => {
    // Ne pas télécharger pour les liens, utiliser handleViewLink à la place
    if (material.type === 'Link') {
      return;
    }
    
    if (material.id) {
      try {
        await incrementResourceDownload(material.id);
      } catch (error) {
      }
    }
    // Ouvrir le lien de téléchargement
    if (material.filePath || material.fileUrl) {
      window.open(material.filePath || material.fileUrl, '_blank');
    }
  };

  // Fonction pour voir/ouvrir un lien (pour les documents de type Link)
  const handleViewLink = (material) => {
    if (material.filePath || material.fileUrl) {
      window.open(material.filePath || material.fileUrl, '_blank');
    }
  };

  // Fonction pour voir une vidéo
  const handleViewVideo = (video) => {
    // Construire l'URL de la vidéo
    let videoUrl = null;
    if (video.youtubeId) {
      videoUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;
    } else if (video.fileUrl) {
      videoUrl = video.fileUrl;
    } else if (video.filePath) {
      videoUrl = video.filePath;
    }
    
    if (videoUrl) {
      window.open(videoUrl, '_blank');
    }
  };

  // Pagination states
  const [currentPageMaterials, setCurrentPageMaterials] = useState(1);
  const [currentPageVideos, setCurrentPageVideos] = useState(1);
  const itemsPerPage = 6;

  // Pagination calculations for materials
  const startIndexMaterials = (currentPageMaterials - 1) * itemsPerPage;
  const endIndexMaterials = startIndexMaterials + itemsPerPage;
  const currentMaterials = materials.slice(startIndexMaterials, endIndexMaterials);
  const totalPagesMaterials = Math.ceil(materials.length / itemsPerPage);

  // Pagination calculations for videos
  const startIndexVideos = (currentPageVideos - 1) * itemsPerPage;
  const endIndexVideos = startIndexVideos + itemsPerPage;
  const currentVideos = videos.slice(startIndexVideos, endIndexVideos);
  const totalPagesVideos = Math.ceil(videos.length / itemsPerPage);

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '18px', color: '#6c757d' }}>{t('materials.loading')}</div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.08);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .material-card-animate {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        .icon-pulse:hover {
          animation: pulse 0.8s ease-in-out infinite;
        }
        .floating-icon {
          animation: float 3s ease-in-out infinite;
        }
            @media (max-width: 768px) {
              .material-card-animate {
                animation-delay: 0s !important;
              }
              .materials-grid,
              .videos-grid {
                grid-template-columns: 1fr !important;
                justify-content: center !important;
              }
            }
            @media (min-width: 769px) and (max-width: 1024px) {
              .materials-grid,
              .videos-grid {
                grid-template-columns: repeat(2, 1fr) !important;
                justify-content: center !important;
              }
            }
            @media (min-width: 1025px) {
              .materials-grid,
              .videos-grid {
                grid-template-columns: repeat(3, 1fr) !important;
                max-width: 1200px !important;
              }
            }
      `}</style>
   <div class="home page-template page-template-page-templates page-template-fullwidth-content page-template-page-templatesfullwidth-content-php page page-id-4059 jet-theme-core jet-theme-core--has-template jet-theme-core--has-header jet-theme-core--has-body jet-theme-core--has-footer layout-fullwidth blog-masonry elementor-default elementor-kit-5747 elementor-page elementor-page-4059"><div id="jet-theme-core-document" class="jet-theme-core-document jet-theme-core-document--elementor-content-type"><div class="jet-theme-core-document__inner">
	
	<Navbar/>
	
	{/* Composant de test des icônes FontAwesome */}
	
	<article id="post-4059" class="post-4059 page type-page status-publish hentry">	
			<div data-elementor-type="wp-page" data-elementor-id="4059" class="elementor elementor-4059">
				<section class="elementor-section  elementor-top-section elementor-element elementor-element-ic2p76x elementor-section-full_width elementor-section-height-default elementor-section-height-default" data-id="ic2p76x" data-element_type="section" data-settings="{&quot;jet_parallax_layout_list&quot;:[{&quot;jet_parallax_layout_image&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;_id&quot;:&quot;5759a04&quot;,&quot;jet_parallax_layout_image_tablet&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_image_mobile&quot;:{&quot;url&quot;:&quot;&quot;,&quot;id&quot;:&quot;&quot;,&quot;size&quot;:&quot;&quot;},&quot;jet_parallax_layout_speed&quot;:{&quot;unit&quot;:&quot;%&quot;,&quot;size&quot;:50,&quot;sizes&quot;:[]},&quot;jet_parallax_layout_type&quot;:&quot;scroll&quot;,&quot;jet_parallax_layout_direction&quot;:null,&quot;jet_parallax_layout_fx_direction&quot;:null,&quot;jet_parallax_layout_z_index&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x&quot;:50,&quot;jet_parallax_layout_bg_x_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_x_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y&quot;:50,&quot;jet_parallax_layout_bg_y_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_y_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size&quot;:&quot;auto&quot;,&quot;jet_parallax_layout_bg_size_tablet&quot;:&quot;&quot;,&quot;jet_parallax_layout_bg_size_mobile&quot;:&quot;&quot;,&quot;jet_parallax_layout_animation_prop&quot;:&quot;transform&quot;,&quot;jet_parallax_layout_on&quot;:[&quot;desktop&quot;,&quot;tablet&quot;]}]}">
					<div class="elementor-container elementor-column-gap-default">
						<div class="elementor-column elementor-col-100 elementor-top-column elementor-element elementor-element-im9c73l" data-id="im9c73l" data-element_type="column">
							<div class="elementor-widget-wrap elementor-element-populated">
								<div class="elementor-element elementor-element-0bd4fc3 elementor-widget elementor-widget-jet-slider" data-id="0bd4fc3" data-element_type="widget" data-settings="{&quot;slider_height&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:852,&quot;sizes&quot;:[]},&quot;_animation&quot;:&quot;none&quot;,&quot;slider_height_tablet&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:650,&quot;sizes&quot;:[]},&quot;slider_height_mobile&quot;:{&quot;unit&quot;:&quot;px&quot;,&quot;size&quot;:500,&quot;sizes&quot;:[]}}" data-widget_type="jet-slider.default">
									<div class="elementor-widget-container">
										<div class="elementor-jet-slider jet-elements">
											<div style={{ position: 'relative', width: '100%', height: '400px', overflow: 'hidden' }}>
                                                <img 
                                                    src="https://images.pexels.com/photos/974314/pexels-photo-974314.jpeg?auto=compress&cs=tinysrgb&w=2070&dpr=1" 
                                                    alt="Agriculture urbaine durable — MedAgriCycle" 
													style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
												/>
												<div style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.45)' }} />
												<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
													<div style={{ background: 'rgba(0,0,0,0.45)', padding: '16px 24px', borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.25)', backdropFilter: 'blur(2px)', maxWidth: '960px', width: '100%' }}>
														<h1 style={{ color: '#fff', fontSize: 'clamp(28px,5vw,46px)', fontWeight: 800, margin: 0, textAlign: 'center', letterSpacing: '0.5px' }}>
                                                        {t('materials.hero.title')}
                                                        </h1>
                                  <p style={{ color: 'rgba(255,255,255,0.95)', fontSize: 'clamp(14px,2.5vw,18px)', lineHeight: 1.6, margin: '12px auto 0', textAlign: 'center', maxWidth: '800px' }}>
																{t('materials.hero.description')}
                                  </p>
                                  
                                

                                

                                                     
														
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
          

         
          <section id="events" className="elementor-section elementor-section-materiels elementor-top-section elementor-element elementor-element-5zcfz9z elementor-section-boxed">
			<div className="elementor-container">
							<div className="elementor-widget-container" style={{ width: '100%', padding: '0' }}>
								
								{/* Filtres communs pour documents et vidéos */}
								<div style={{ padding: '6px 16px 40px', position: 'relative', overflow: 'hidden' }}>
									<div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
										{/* Filtres */}
										<div style={{
											background: 'white',
											borderRadius: '16px',
											padding: '24px',
											marginBottom: '32px',
											boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
											border: '1px solid #e5e7eb'
										}}>
											<div style={{
												display: 'grid',
												gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
												gap: '16px',
												marginBottom: '16px'
											}}>
												{/* Recherche */}
												<div>
													<label style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: 600,
														color: '#374151'
													}}>
														<i className="fas fa-search" style={{ marginRight: '8px', color: '#96ca2d' }}></i>
														{t('materials.filters.search')}
													</label>
													<input
														type="text"
														value={searchTerm}
														onChange={(e) => setSearchTerm(e.target.value)}
														placeholder={t('materials.filters.searchPlaceholder')}
														style={{
															width: '100%',
															padding: '10px 14px',
															border: '2px solid #e5e7eb',
															borderRadius: '8px',
															fontSize: '14px',
															transition: 'all 0.3s ease'
														}}
														onFocus={(e) => {
															e.target.style.borderColor = '#96ca2d';
															e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
														}}
														onBlur={(e) => {
															e.target.style.borderColor = '#e5e7eb';
															e.target.style.boxShadow = 'none';
														}}
													/>
                    </div>
												
												{/* Filtre par type */}
												<div>
													<label style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: 600,
														color: '#374151'
													}}>
														<i className="fas fa-file" style={{ marginRight: '8px', color: '#96ca2d' }}></i>
														{t('materials.filters.type')}
													</label>
													<select
														value={typeFilter}
														onChange={(e) => setTypeFilter(e.target.value)}
														style={{
															width: '100%',
															padding: '10px 14px',
															border: '2px solid #e5e7eb',
															borderRadius: '8px',
															fontSize: '14px',
															background: 'white',
															cursor: 'pointer',
															transition: 'all 0.3s ease'
														}}
														onFocus={(e) => {
															e.target.style.borderColor = '#96ca2d';
															e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
														}}
														onBlur={(e) => {
															e.target.style.borderColor = '#e5e7eb';
															e.target.style.boxShadow = 'none';
														}}
													>
														<option value="">{t('materials.filters.allTypes')}</option>
														{allTypes.map(type => (
															<option key={type} value={type}>{type}</option>
														))}
													</select>
										</div>
										
												{/* Filtre par date */}
												<div>
													<label style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: 600,
														color: '#374151'
													}}>
														<i className="fas fa-calendar-alt" style={{ marginRight: '8px', color: '#96ca2d' }}></i>
														{t('materials.filters.date')}
													</label>
													<div style={{ position: 'relative' }}>
														<input
															type="date"
															value={dateFilter}
															onChange={(e) => setDateFilter(e.target.value)}
															style={{
																width: '100%',
																padding: '10px 14px',
																border: '2px solid #e5e7eb',
																borderRadius: '8px',
																fontSize: '14px',
																background: 'white',
																cursor: 'pointer',
																transition: 'all 0.3s ease',
																color: dateFilter ? '#1f2937' : '#9ca3af',
																WebkitAppearance: 'none',
																MozAppearance: 'textfield'
															}}
															onFocus={(e) => {
																e.target.style.borderColor = '#96ca2d';
																e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
															}}
															onBlur={(e) => {
																e.target.style.borderColor = '#e5e7eb';
																e.target.style.boxShadow = 'none';
															}}
															placeholder={t('materials.filters.datePlaceholder')}
														/>
														{dateFilter && (
															<button
																type="button"
																onClick={() => setDateFilter('')}
																style={{
																	position: 'absolute',
																	right: '8px',
																	top: '50%',
																	transform: 'translateY(-50%)',
																	background: 'transparent',
																	border: 'none',
																	cursor: 'pointer',
																	padding: '4px',
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	color: '#9ca3af',
																	transition: 'color 0.3s ease'
																}}
																onMouseEnter={(e) => {
																	e.target.style.color = '#ef4444';
																}}
																onMouseLeave={(e) => {
																	e.target.style.color = '#9ca3af';
																}}
																title={t('materials.filters.clearDate')}
															>
																<i className="fas fa-times-circle" style={{ fontSize: '16px' }}></i>
															</button>
														)}
													</div>
												</div>
												
												{/* Tri */}
												<div>
													<label style={{
														display: 'block',
														marginBottom: '8px',
														fontSize: '14px',
														fontWeight: 600,
														color: '#374151'
													}}>
														<i className="fas fa-sort" style={{ marginRight: '8px', color: '#96ca2d' }}></i>
														{t('materials.filters.sortBy')}
													</label>
													<select
														value={sortBy}
														onChange={(e) => setSortBy(e.target.value)}
														style={{
															width: '100%',
															padding: '10px 14px',
															border: '2px solid #e5e7eb',
															borderRadius: '8px',
															fontSize: '14px',
															background: 'white',
															cursor: 'pointer',
															transition: 'all 0.3s ease'
														}}
														onFocus={(e) => {
															e.target.style.borderColor = '#96ca2d';
															e.target.style.boxShadow = '0 0 0 3px rgba(150, 202, 45, 0.1)';
														}}
														onBlur={(e) => {
															e.target.style.borderColor = '#e5e7eb';
															e.target.style.boxShadow = 'none';
														}}
													>
														<option value="newest">{t('materials.filters.sortOptions.newest')}</option>
														<option value="oldest">{t('materials.filters.sortOptions.oldest')}</option>
														<option value="name">{t('materials.filters.sortOptions.name')}</option>
													</select>
												</div>
											</div>
											
											{/* Bouton réinitialiser */}
											{(searchTerm || typeFilter || dateFilter) && (
												<div style={{ textAlign: 'center', marginTop: '16px' }}>
													<button
														onClick={() => {
															setSearchTerm('');
															setTypeFilter('');
															setDateFilter('');
														}}
														style={{
															padding: '10px 24px',
															background: '#f3f4f6',
															color: '#374151',
															border: '2px solid #e5e7eb',
															borderRadius: '8px',
															cursor: 'pointer',
															fontSize: '14px',
															fontWeight: 600,
															transition: 'all 0.3s ease'
														}}
														onMouseEnter={(e) => {
															e.target.style.background = '#e5e7eb';
															e.target.style.borderColor = '#96ca2d';
														}}
														onMouseLeave={(e) => {
															e.target.style.background = '#f3f4f6';
															e.target.style.borderColor = '#e5e7eb';
														}}
													>
														<i className="fas fa-times" style={{ marginRight: '8px' }}></i>
														{t('materials.filters.reset')}
													</button>
												</div>
											)}
										</div>
									</div>
								</div>
								
								{/* Available Materials Section - Enhanced Design */}
								<div style={{ padding: '6px 16px 80px', position: 'relative', overflow: 'hidden' }}>
									<div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
										<div style={{ textAlign: 'center', marginBottom: '40px' }}>
											<div style={{ display: 'inline-block', padding: '12px 32px', background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)', borderRadius: '30px', marginBottom: '24px', boxShadow: '0 6px 20px rgba(150,202,45,0.4)', border: '2px solid rgba(255,255,255,0.3)' }}>
												<span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{t('materials.documents.badge')}</span>
                    </div>
											<h2 style={{ fontSize: 'clamp(32px,5vw,48px)', color: '#2c3e50', margin: 0, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.2 }}>
												{t('materials.documents.title')}
											</h2>
											<p style={{ color: '#6c757d', fontSize: '1.15rem', marginTop: '16px', maxWidth: '650px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7 }}>
												{t('materials.documents.subtitle')}
											</p>
										</div>
										
										{materials.length === 0 ? (
											<div style={{ textAlign: 'center', padding: '60px 20px' }}>
												<div style={{ fontSize: '18px', color: '#6c757d' }}>
													{(searchTerm || typeFilter || dateFilter) 
														? t('materials.documents.noResults')
														: t('materials.documents.empty')}
												</div>
											</div>
										) : (
											<>
										<div className="materials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '28px', justifyContent: 'center', maxWidth: '1200px', margin: '0 auto' }}>
											{currentMaterials.map((material, index) => {
												const isLink = material.type === 'Link';
												return (
												<div
													key={index}
													className="material-card-animate"
                      style={{
														background: '#fff',
														borderRadius: '12px',
														padding: '28px',
														boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
														transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
														display: 'flex',
														flexDirection: 'column',
														border: '1px solid #e9ecef',
														position: 'relative',
														overflow: 'hidden',
														animationDelay: `${index * 0.1}s`
													}}
													onMouseEnter={(e) => {
														e.currentTarget.style.transform = 'translateY(-8px)';
														e.currentTarget.style.boxShadow = '0 12px 32px rgba(150,202,45,0.2)';
														e.currentTarget.style.borderColor = '#96ca2d';
													}}
													onMouseLeave={(e) => {
														e.currentTarget.style.transform = 'translateY(0)';
														e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
														e.currentTarget.style.borderColor = '#e9ecef';
													}}
												>
													{/* Top accent bar */}
													<div style={{ 
														position: 'absolute',
														top: 0,
														left: 0,
														right: 0,
														height: '4px',
														background: 'linear-gradient(90deg, #96ca2d 0%, #7aa022 100%)'
													}}></div>
													
													{/* Icon and title row */}
													<div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
                  <div style={{
															width: '56px',
															height: '56px',
															background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)',
															borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
															flexShrink: 0,
															boxShadow: '0 4px 12px rgba(150,202,45,0.3)'
                    }}>
															<i className={`fas ${material.icon}`} style={{ color: '#fff', fontSize: '24px' }}></i>
                    </div>
														<div style={{ flex: 1 }}>
															<h4 style={{ 
																color: '#2c3e50', 
																fontSize: '1.25rem', 
																margin: 0, 
																marginBottom: '16px',
																fontWeight: 700,
																lineHeight: 1.4,
																minHeight: '60px',
																display: 'flex',
																alignItems: 'flex-start'
															}}>
																{material.name}
															</h4>
															<span style={{ 
																display: 'inline-block',
																padding: '4px 10px',
																background: '#f0f7e6',
                        color: '#96ca2d',
																borderRadius: '6px',
																fontSize: '0.8rem',
																fontWeight: 600
															}}>
																{material.type}
															</span>
														</div>
                  </div>

													{/* Date info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
														gap: '12px',
														color: '#6c757d',
														fontSize: '0.9rem',
                      marginBottom: '20px',
                      flexWrap: 'wrap',
                      height: '26px',
                      minHeight: '26px'
                    }}>
														{material.createdAt && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																<span>{material.createdAt}</span>
															</span>
														)}
														{material.updatedAt && material.updatedAt !== material.createdAt && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																<i className="fas fa-calendar-check" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																<span>{t('materials.documents.modified')} {material.updatedAt}</span>
															</span>
														)}
														{!material.createdAt && !material.updatedAt && material.uploadDate && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																<span>{material.uploadDate}</span>
															</span>
														)}
														{!material.createdAt && !material.updatedAt && !material.uploadDate && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '6px', visibility: 'hidden' }}>
																<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																<span>Placeholder</span>
															</span>
														)}
                </div>

													{/* Download/View Link button */}
													<div style={{ 
														marginTop: 'auto',
														paddingTop: '16px',
														borderTop: '1px solid #e9ecef'
													}}>
														{isLink ? (
															<div 
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleViewLink(material);
																}}
																style={{
																	display: 'inline-flex',
																	alignItems: 'center',
																	gap: '8px',
																	color: '#96ca2d',
																	fontWeight: 600,
																	fontSize: '0.95rem',
																	transition: 'all 0.3s ease',
																	cursor: 'pointer'
																}}
																onMouseEnter={(e) => {
																	e.currentTarget.style.gap = '12px';
																	e.currentTarget.style.color = '#7aa022';
																	const icon = e.currentTarget.querySelector('i');
																	if (icon) icon.style.transform = 'translateX(4px)';
																}}
																onMouseLeave={(e) => {
																	e.currentTarget.style.gap = '8px';
																	e.currentTarget.style.color = '#96ca2d';
																	const icon = e.currentTarget.querySelector('i');
																	if (icon) icon.style.transform = 'translateX(0)';
																}}
															>
																<span>{t('materials.documents.viewLink')}</span>
																<i className="fas fa-external-link-alt" style={{ transition: 'transform 0.3s ease', fontSize: '14px' }}></i>
															</div>
														) : (
															<div 
																onClick={(e) => {
																	e.preventDefault();
																	e.stopPropagation();
																	handleDownload(material);
																}}
																style={{
																	display: 'inline-flex',
																	alignItems: 'center',
																	gap: '8px',
																	color: '#96ca2d',
																	fontWeight: 600,
																	fontSize: '0.95rem',
																	transition: 'all 0.3s ease',
																	cursor: 'pointer'
																}}
																onMouseEnter={(e) => {
																	e.currentTarget.style.gap = '12px';
																	e.currentTarget.style.color = '#7aa022';
																	const icon = e.currentTarget.querySelector('i');
																	if (icon) icon.style.transform = 'translateY(4px)';
																}}
																onMouseLeave={(e) => {
																	e.currentTarget.style.gap = '8px';
																	e.currentTarget.style.color = '#96ca2d';
																	const icon = e.currentTarget.querySelector('i');
																	if (icon) icon.style.transform = 'translateY(0)';
																}}
															>
																<span>{t('materials.documents.download')}</span>
																<i className="fas fa-download" style={{ transition: 'transform 0.3s ease', fontSize: '14px' }}></i>
															</div>
														)}
													</div>
												</div>
												);
											})}
										</div>
										
										{/* Pagination pour les documents */}
										{materials.length > itemsPerPage && (
											<div style={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												gap: 0,
												marginTop: '40px',
												flexWrap: 'wrap'
											}}>
												<div style={{ display: 'inline-flex', gap: 0 }}>
													<button
														onClick={() => setCurrentPageMaterials(prev => Math.max(1, prev - 1))}
														disabled={currentPageMaterials === 1}
														style={{
															padding: '8px 10px',
															minWidth: 36,
															borderRadius: '6px 0 0 6px',
															border: '1px solid #96ca2d',
															background: currentPageMaterials === 1 ? '#f2f2f2' : '#96ca2d',
															color: currentPageMaterials === 1 ? '#888' : '#fff',
															cursor: currentPageMaterials === 1 ? 'not-allowed' : 'pointer',
															margin: 0,
															borderRightWidth: 0,
															fontSize: '14px'
														}}
													>&lt;</button>
													{(() => {
														const buttons = [];
														const renderBtn = (i, isFirst = false) => (
															<button
																key={i}
																onClick={() => setCurrentPageMaterials(i)}
																style={{
																	padding: '8px 10px',
																	minWidth: 36,
																	borderRadius: 0,
																	border: `1px solid ${currentPageMaterials === i ? '#96ca2d' : '#ddd'}`,
																	background: currentPageMaterials === i ? '#96ca2d' : '#fff',
																	color: currentPageMaterials === i ? '#fff' : '#333',
																	cursor: 'pointer',
																	margin: 0,
																	borderLeftWidth: isFirst ? '1px' : 0,
																	fontSize: '14px'
																}}
															>
																{i}
															</button>
														);

														const shown = Math.min(totalPagesMaterials, 3);
														for (let i = 1; i <= shown; i++) {
															buttons.push(renderBtn(i, i === 1));
														}
														if (totalPagesMaterials > 3) {
															buttons.push(
																<span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0, fontSize: '14px', display: 'flex', alignItems: 'center' }}>...</span>
															);
														}
														return buttons;
													})()}
													<button
														onClick={() => setCurrentPageMaterials(prev => Math.min(totalPagesMaterials, prev + 1))}
														disabled={currentPageMaterials === totalPagesMaterials}
														style={{
															padding: '8px 10px',
															minWidth: 36,
															borderRadius: '0 6px 6px 0',
															border: '1px solid #96ca2d',
															background: currentPageMaterials === totalPagesMaterials ? '#f2f2f2' : '#96ca2d',
															color: currentPageMaterials === totalPagesMaterials ? '#888' : '#fff',
															cursor: currentPageMaterials === totalPagesMaterials ? 'not-allowed' : 'pointer',
															margin: 0,
															borderLeftWidth: 0,
															fontSize: '14px'
														}}
													>&gt;</button>
												</div>
											</div>
										)}
											</>
										)}
									</div>
								</div>

								{/* Videos Section - Enhanced Design */}
								<div style={{ padding: '6px 16px 80px', position: 'relative', overflow: 'hidden' }}>
									<div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
										<div style={{ textAlign: 'center', marginBottom: '40px' }}>
											<div style={{ display: 'inline-block', padding: '12px 32px', background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)', borderRadius: '30px', marginBottom: '24px', boxShadow: '0 6px 20px rgba(150,202,45,0.5)', border: '2px solid rgba(255,255,255,0.2)' }}>
												<span style={{ color: '#fff', fontSize: '0.85rem', fontWeight: 700, letterSpacing: '1.5px', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>{t('materials.videos.badge')}</span>
											</div>
											<h2 style={{ fontSize: 'clamp(32px,5vw,48px)', color: '#2c3e50', margin: 0, fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.2 }}>
												{t('materials.videos.title')}
											</h2>
											<p style={{ color: '#6c757d', fontSize: '1.15rem', marginTop: '16px', maxWidth: '650px', marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.7 }}>
												{t('materials.videos.subtitle')}
											</p>
										</div>
										
										{videos.length === 0 ? (
											<div style={{ textAlign: 'center', padding: '60px 20px' }}>
												<div style={{ fontSize: '18px', color: '#6c757d' }}>
													{(searchTerm || typeFilter || dateFilter) 
														? t('materials.videos.noResults')
														: t('materials.videos.empty')}
												</div>
											</div>
										) : (
											<>
										<div className="videos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '28px', justifyContent: 'center', maxWidth: '1200px', margin: '0 auto' }}>
											{currentVideos.map((video, index) => (
												<div
													key={index}
													className="material-card-animate"
													style={{
														background: '#fff',
														borderRadius: '12px',
														padding: '28px',
														boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
														transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
														display: 'flex',
														flexDirection: 'column',
														border: '1px solid #e9ecef',
														position: 'relative',
														overflow: 'hidden',
														animationDelay: `${index * 0.1}s`
													}}
													onMouseEnter={(e) => {
														e.currentTarget.style.transform = 'translateY(-8px)';
														e.currentTarget.style.boxShadow = '0 12px 32px rgba(150,202,45,0.2)';
														e.currentTarget.style.borderColor = '#96ca2d';
													}}
													onMouseLeave={(e) => {
														e.currentTarget.style.transform = 'translateY(0)';
														e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
														e.currentTarget.style.borderColor = '#e9ecef';
													}}
												>
													{/* Top accent bar */}
													<div style={{ 
														position: 'absolute',
														top: 0,
														left: 0,
														right: 0,
														height: '4px',
														background: 'linear-gradient(90deg, #96ca2d 0%, #7aa022 100%)'
													}}></div>
													
													{/* Icon and title row */}
													<div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
														<div style={{
															width: '56px',
															height: '56px',
															background: 'linear-gradient(135deg, #96ca2d 0%, #7aa022 100%)',
															borderRadius: '12px',
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															flexShrink: 0,
															boxShadow: '0 4px 12px rgba(150,202,45,0.3)'
														}}>
															<i className="fas fa-video" style={{ color: '#fff', fontSize: '24px' }}></i>
														</div>
														<div style={{ flex: 1 }}>
															<h4 style={{ 
																color: '#2c3e50', 
																fontSize: '1.25rem', 
																margin: 0, 
																marginBottom: '16px',
																fontWeight: 700,
																lineHeight: 1.4,
																minHeight: '60px',
																display: 'flex',
																alignItems: 'flex-start'
															}}>
																{video.name}
															</h4>
															<span style={{ 
																display: 'inline-block',
																padding: '4px 10px',
																background: '#f0f7e6',
																color: '#96ca2d',
																borderRadius: '6px',
																fontSize: '0.8rem',
																fontWeight: 600
															}}>
																{video.type || 'Video'}
															</span>
														</div>
													</div>

													{/* Video Frame */}
													<div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', backgroundColor: '#000', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
														{(() => {
															// Construire l'URL YouTube à partir de youtubeId ou fileUrl
															let videoUrl = null;
															if (video.youtubeId) {
																videoUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;
															} else if (video.fileUrl) {
																videoUrl = video.fileUrl;
															} else if (video.filePath) {
																videoUrl = video.filePath;
															}
															
															// Vérifier si c'est une URL YouTube
															const isYouTube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
															
															if (isYouTube) {
																return (
																	<iframe 
																		src={convertYouTubeUrl(videoUrl)} 
																		title={`MedAgriCycle — ${video.name}`}
																		style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, zIndex: 0 }} 
																		allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
																		allowFullScreen
																	></iframe>
																);
															} else if (videoUrl) {
																return (
																	<video 
																		src={videoUrl}
																		controls
																		style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', backgroundColor: '#000' }}
																	>
																		{t('materials.videos.browserNotSupported')}
																	</video>
																);
															} else {
																return (
																	<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
																		<i className="fas fa-video" style={{ fontSize: '48px', opacity: 0.5 }}></i>
																	</div>
																);
															}
														})()}
													</div>

													{/* Date info */}
													<div style={{
														display: 'flex',
														alignItems: 'center',
														gap: '12px',
														color: '#6c757d',
														fontSize: '0.9rem',
														marginBottom: '20px',
														flexWrap: 'wrap',
														height: '26px',
														minHeight: '26px'
													}}>
															{video.createdAt && (
																<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																	<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																	<span style={{ fontWeight: 500 }}>{video.createdAt}</span>
																</span>
															)}
															{video.updatedAt && video.updatedAt !== video.createdAt && (
																<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																	<i className="fas fa-calendar-check" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																	<span style={{ fontWeight: 500 }}>{t('materials.videos.modified')} {video.updatedAt}</span>
																</span>
															)}
															{!video.createdAt && !video.updatedAt && video.uploadDate && (
																<span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
																	<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																	<span style={{ fontWeight: 500 }}>{video.uploadDate}</span>
																</span>
															)}
														{!video.createdAt && !video.updatedAt && !video.uploadDate && (
															<span style={{ display: 'flex', alignItems: 'center', gap: '6px', visibility: 'hidden' }}>
																<i className="fas fa-calendar-alt" style={{ color: '#96ca2d', fontSize: '14px' }}></i>
																<span>Placeholder</span>
															</span>
														)}
													</div>
													
													{/* View Video / Download button */}
													<div style={{ 
														marginTop: 'auto',
														paddingTop: '16px',
														borderTop: '1px solid #e9ecef',
														display: 'flex',
														gap: '12px',
														flexWrap: 'wrap'
													}}>
														{(() => {
															// Construire l'URL de la vidéo
															let videoUrl = null;
															if (video.youtubeId) {
																videoUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`;
															} else if (video.fileUrl) {
																videoUrl = video.fileUrl;
															} else if (video.filePath) {
																videoUrl = video.filePath;
															}
															
															// Vérifier si c'est une URL YouTube
															const isYouTube = videoUrl && (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be'));
															
															return (
																<>
																	{/* Bouton "Voir la vidéo" pour toutes les vidéos */}
																	{videoUrl && (
																		<div 
																			onClick={(e) => {
																				e.preventDefault();
																				e.stopPropagation();
																				handleViewVideo(video);
																			}}
																			style={{
																				display: 'inline-flex',
																				alignItems: 'center',
																				gap: '8px',
																				color: '#96ca2d',
																				fontWeight: 600,
																				fontSize: '0.95rem',
																				transition: 'all 0.3s ease',
																				cursor: 'pointer'
																			}}
																			onMouseEnter={(e) => {
																				e.currentTarget.style.gap = '12px';
																				e.currentTarget.style.color = '#7aa022';
																				const icon = e.currentTarget.querySelector('i');
																				if (icon) icon.style.transform = 'translateX(4px)';
																			}}
																			onMouseLeave={(e) => {
																				e.currentTarget.style.gap = '8px';
																				e.currentTarget.style.color = '#96ca2d';
																				const icon = e.currentTarget.querySelector('i');
																				if (icon) icon.style.transform = 'translateX(0)';
																			}}
																		>
																			<span>{t('materials.videos.viewVideo')}</span>
																			<i className="fas fa-external-link-alt" style={{ transition: 'transform 0.3s ease', fontSize: '14px' }}></i>
																		</div>
																	)}
																	
																	{/* Bouton "Download" seulement pour les vidéos non-YouTube uploadées */}
																	{!isYouTube && videoUrl && (
																		<div 
																			onClick={(e) => {
																				e.preventDefault();
																				e.stopPropagation();
																				handleDownload(video);
																			}}
																			style={{
																				display: 'inline-flex',
																				alignItems: 'center',
																				gap: '8px',
																				color: '#96ca2d',
																				fontWeight: 600,
																				fontSize: '0.95rem',
																				transition: 'all 0.3s ease',
																				cursor: 'pointer'
																			}}
																			onMouseEnter={(e) => {
																				e.currentTarget.style.gap = '12px';
																				e.currentTarget.style.color = '#7aa022';
																				const icon = e.currentTarget.querySelector('i');
																				if (icon) icon.style.transform = 'translateY(-2px)';
																			}}
																			onMouseLeave={(e) => {
																				e.currentTarget.style.gap = '8px';
																				e.currentTarget.style.color = '#96ca2d';
																				const icon = e.currentTarget.querySelector('i');
																				if (icon) icon.style.transform = 'translateY(0)';
																			}}
																		>
																			<span>{t('materials.documents.download')}</span>
																			<i className="fas fa-download" style={{ transition: 'transform 0.3s ease', fontSize: '14px' }}></i>
																		</div>
																	)}
																</>
															);
														})()}
													</div>
												</div>
											))}
										</div>
										
										{/* Pagination pour les vidéos */}
										{videos.length > itemsPerPage && (
											<div style={{
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												gap: 0,
												marginTop: '40px',
												flexWrap: 'wrap'
											}}>
												<div style={{ display: 'inline-flex', gap: 0 }}>
													<button
														onClick={() => setCurrentPageVideos(prev => Math.max(1, prev - 1))}
														disabled={currentPageVideos === 1}
														style={{
															padding: '8px 10px',
															minWidth: 36,
															borderRadius: '6px 0 0 6px',
															border: '1px solid #96ca2d',
															background: currentPageVideos === 1 ? '#f2f2f2' : '#96ca2d',
															color: currentPageVideos === 1 ? '#888' : '#fff',
															cursor: currentPageVideos === 1 ? 'not-allowed' : 'pointer',
															margin: 0,
															borderRightWidth: 0,
															fontSize: '14px'
														}}
													>&lt;</button>
													{(() => {
														const buttons = [];
														const renderBtn = (i, isFirst = false) => (
															<button
																key={i}
																onClick={() => setCurrentPageVideos(i)}
																style={{
																	padding: '8px 10px',
																	minWidth: 36,
																	borderRadius: 0,
																	border: `1px solid ${currentPageVideos === i ? '#96ca2d' : '#ddd'}`,
																	background: currentPageVideos === i ? '#96ca2d' : '#fff',
																	color: currentPageVideos === i ? '#fff' : '#333',
																	cursor: 'pointer',
																	margin: 0,
																	borderLeftWidth: isFirst ? '1px' : 0,
																	fontSize: '14px'
																}}
															>
																{i}
															</button>
														);

														const shown = Math.min(totalPagesVideos, 3);
														for (let i = 1; i <= shown; i++) {
															buttons.push(renderBtn(i, i === 1));
														}
														if (totalPagesVideos > 3) {
															buttons.push(
																<span key="ell" style={{ padding: '8px 6px', border: '1px solid #ddd', borderLeftWidth: 0, fontSize: '14px', display: 'flex', alignItems: 'center' }}>...</span>
															);
														}
														return buttons;
													})()}
													<button
														onClick={() => setCurrentPageVideos(prev => Math.min(totalPagesVideos, prev + 1))}
														disabled={currentPageVideos === totalPagesVideos}
														style={{
															padding: '8px 10px',
															minWidth: 36,
															borderRadius: '0 6px 6px 0',
															border: '1px solid #96ca2d',
															background: currentPageVideos === totalPagesVideos ? '#f2f2f2' : '#96ca2d',
															color: currentPageVideos === totalPagesVideos ? '#888' : '#fff',
															cursor: currentPageVideos === totalPagesVideos ? 'not-allowed' : 'pointer',
															margin: 0,
															borderLeftWidth: 0,
															fontSize: '14px'
														}}
													>&gt;</button>
												</div>
											</div>
										)}
											</>
										)}
									</div>
                </div>
              </div>
            </div>
          </section>
			</div>
		</article>
		</div>
	</div>
</div>
      <Footer />
    </>
  );
};

export default Materials;
